const formatMinutes = (minutes) => {
    const value = Number(minutes) || 0;
    const hours = Math.floor(value / 60);
    const remain = value % 60;
    if (hours && remain) {
        return `${hours}時間${remain}分`;
    }
    if (hours) {
        return `${hours}時間`;
    }
    return `${remain}分`;
};

const escapeHtml = (value = '') => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

class StandardTimeApp {
    constructor() {
        this.root = document.getElementById('standard-time-app');
        if (!this.root) {
            return;
        }

        this.state = {
            loading: true,
            saving: false,
            error: null,
            success: null,
            courses: [],
            histories: [],
            selectedCourseId: '',
            selectedChapterId: '',
            form: {},
        };

        this.fetchOptions();
    }

    async fetchOptions() {
        this.setState({ loading: true, error: null, success: null });
        const url = this.root.dataset.optionsUrl;

        try {
            const response = await window.axios.get(url);
            const { courses = [] } = response.data || {};
            const selectedCourseId = this.state.selectedCourseId || (courses[0] ? String(courses[0].id) : '');
            const selectedCourse = courses.find((course) => String(course.id) === String(selectedCourseId));
            let selectedChapterId = this.state.selectedChapterId;

            if (selectedCourse && selectedCourse.chapters && selectedCourse.chapters.length > 0) {
                const hasCurrent = selectedCourse.chapters.some((chapter) => String(chapter.id) === String(selectedChapterId));
                if (!hasCurrent) {
                    selectedChapterId = String(selectedCourse.chapters[0].id);
                }
            } else {
                selectedChapterId = '';
            }

            const form = this.buildForm(selectedCourseId, selectedChapterId, courses);

            this.setState({
                loading: false,
                error: null,
                courses,
                selectedCourseId,
                selectedChapterId,
                histories: this.sanitizeHistories(response.data?.histories ?? []),
                form,
            });
        } catch (error) {
            this.setState({
                loading: false,
                error: this.extractErrorMessage(error),
            });
        }
    }

    buildForm(courseId, chapterId, courses) {
        const numericCourseId = Number(courseId);
        if (!numericCourseId) {
            return {};
        }

        const sourceCourses = courses || this.state.courses;
        const course = sourceCourses.find((item) => item.id === numericCourseId);
        if (!course || !course.chapters) {
            return {};
        }

        const numericChapterId = Number(chapterId);
        const chapter = course.chapters.find((item) => item.id === numericChapterId);
        if (!chapter) {
            return {};
        }

        const form = {};
        chapter.texts.forEach((text) => {
            form[String(text.id)] = Number(text.standard_minutes) || 0;
        });
        return form;
    }

    setState(patch, options = {}) {
        this.state = { ...this.state, ...patch };
        if (options.skipRender) {
            return;
        }
        this.render();
    }

    extractErrorMessage(error) {
        if (!error) {
            return '予期せぬエラーが発生しました。';
        }
        if (error.response && error.response.data) {
            if (typeof error.response.data === 'string') {
                return error.response.data;
            }
            if (error.response.data.message) {
                return error.response.data.message;
            }
        }
        if (error.message) {
            return error.message;
        }
        return '予期せぬエラーが発生しました。';
    }

    getSelectedCourse() {
        const courseId = Number(this.state.selectedCourseId);
        if (!courseId) {
            return null;
        }
        return this.state.courses.find((course) => course.id === courseId) || null;
    }

    getSelectedChapter() {
        const course = this.getSelectedCourse();
        const chapterId = Number(this.state.selectedChapterId);
        if (!course || !chapterId || !course.chapters) {
            return null;
        }
        return course.chapters.find((chapter) => chapter.id === chapterId) || null;
    }

    handleCourseChange(event) {
        const value = event.target.value;
        const course = this.state.courses.find((item) => String(item.id) === String(value));
        const chapterId = course && course.chapters && course.chapters.length > 0
            ? String(course.chapters[0].id)
            : '';
        const form = this.buildForm(value, chapterId, this.state.courses);
        this.setState({ selectedCourseId: value, selectedChapterId: chapterId, form });
    }

    handleChapterChange(event) {
        const value = event.target.value;
        const form = this.buildForm(this.state.selectedCourseId, value, this.state.courses);
        this.setState({ selectedChapterId: value, form });
    }

    handleMinutesChange(event) {
        const textId = event.target.dataset.textId;
        const value = Number(event.target.value);
        const sanitized = Number.isFinite(value) && value >= 0 ? Math.round(value) : 0;
        event.target.value = sanitized;
        this.setState({
            form: {
                ...this.state.form,
                [textId]: sanitized,
            },
        }, { skipRender: true });
        this.updateSummary();
    }

    async handleSave() {
        if (this.state.saving) {
            return;
        }

        const course = this.getSelectedCourse();
        if (!course) {
            this.setState({ error: 'コースを選択してください。' });
            return;
        }

        const chapter = this.getSelectedChapter();
        if (!chapter) {
            this.setState({ error: '章を選択してください。' });
            return;
        }

        const payload = {
            course_id: Number(this.state.selectedCourseId),
            chapter_id: Number(this.state.selectedChapterId),
            texts: chapter.texts.map((text) => ({
                id: text.id,
                standard_minutes: Number(this.state.form[String(text.id)]) || 0,
            })),
        };

        this.setState({ saving: true, error: null, success: null });

        try {
            const response = await window.axios.put(this.root.dataset.updateUrl, payload);
            const { courses = [], histories = [] } = response.data || {};
            const filteredHistories = this.sanitizeHistories(histories);
            const courseId = this.state.selectedCourseId;
            let chapterId = this.state.selectedChapterId;
            const updatedCourse = courses.find((item) => String(item.id) === String(courseId));
            if (updatedCourse && updatedCourse.chapters && updatedCourse.chapters.length > 0) {
                const exists = updatedCourse.chapters.some((item) => String(item.id) === String(chapterId));
                if (!exists) {
                    chapterId = String(updatedCourse.chapters[0].id);
                }
            } else {
                chapterId = '';
            }

            const form = this.buildForm(courseId, chapterId, courses);

            this.setState({
                saving: false,
                success: '基準時間を更新しました。',
                courses,
                histories: filteredHistories,
                selectedChapterId: chapterId,
                form,
            });
        } catch (error) {
            this.setState({
                saving: false,
                error: this.extractErrorMessage(error),
            });
        }
    }

    sanitizeHistories(histories) {
        if (!Array.isArray(histories)) {
            return [];
        }

        return histories
            .filter((history) => (history.old_minutes ?? 0) !== (history.new_minutes ?? 0))
            .slice(0, 10);
    }

    updateSummary() {
        const summary = this.computeSummary();
        const textCount = this.root.querySelector('[data-summary="count"]');
        const total = this.root.querySelector('[data-summary="total"]');
        const average = this.root.querySelector('[data-summary="average"]');

        if (textCount) {
            textCount.textContent = `${summary.textCount} 件`;
        }
        if (total) {
            total.textContent = formatMinutes(summary.totalMinutes);
        }
        if (average) {
            average.textContent = formatMinutes(summary.average);
        }
    }

    computeSummary() {
        const chapter = this.getSelectedChapter();
        if (!chapter) {
            return { totalMinutes: 0, textCount: 0, average: 0 };
        }

        const minutes = chapter.texts.map((text) => Number(this.state.form[String(text.id)]) || 0);
        const totalMinutes = minutes.reduce((acc, cur) => acc + cur, 0);
        const textCount = minutes.length;
        const average = textCount ? Math.round(totalMinutes / textCount) : 0;
        return { totalMinutes, textCount, average };
    }

    renderLoading() {
        this.root.innerHTML = '<div class="text-center py-5 text-muted">読み込み中...</div>';
    }

    render() {
        if (this.state.loading) {
            this.renderLoading();
            return;
        }

        const course = this.getSelectedCourse();
        const chapter = this.getSelectedChapter();
        const summary = this.computeSummary();

        const courseOptions = this.state.courses.map((courseItem) => (
            `<option value="${courseItem.id}" ${String(courseItem.id) === String(this.state.selectedCourseId) ? 'selected' : ''}>${escapeHtml(courseItem.course_name)}</option>`
        )).join('');

        const chapterOptions = course && course.chapters
            ? course.chapters.map((chapterItem) => (
                `<option value="${chapterItem.id}" ${String(chapterItem.id) === String(this.state.selectedChapterId) ? 'selected' : ''}>${escapeHtml(chapterItem.chapter_name)}</option>`
            )).join('')
            : '';

        const chapterSelectDisabled = !(course && course.chapters && course.chapters.length > 0);
        const chapterPositionLabel = chapter ? escapeHtml(String(chapter.chapter_position)) : '';
        const chapterNameLabel = chapter ? escapeHtml(chapter.chapter_name) : '';

        const rows = chapter
            ? chapter.texts.map((text, index) => `
                    <tr>
                        <td>${index + 1}</td>
                        <td>
                            <div class="fw-bold">${escapeHtml(text.text_name)}</div>
                            <div class="text-muted small">${chapterNameLabel}</div>
                        </td>
                        <td class="text-center">${chapterPositionLabel}</td>
                        <td class="text-center">${escapeHtml(String(text.text_position))}</td>
                        <td>
                            <input type="number" min="0" step="5" value="${this.state.form[String(text.id)] ?? 0}" data-text-id="${text.id}" class="standard-time__input" />
                        </td>
                    </tr>
                `).join('')
            : (course
                ? '<tr><td colspan="5" class="text-center py-4 text-muted">章を選択するとテキスト一覧が表示されます。</td></tr>'
                : '<tr><td colspan="5" class="text-center py-4 text-muted">コースを選択するとテキスト一覧が表示されます。</td></tr>');

        const alerts = `
            ${this.state.error ? `<div class="standard-time__error">${escapeHtml(this.state.error)}</div>` : ''}
            ${this.state.success ? `<div class="standard-time__success">${escapeHtml(this.state.success)}</div>` : ''}
        `;

        const historyItems = this.state.histories && this.state.histories.length
            ? this.state.histories.map((history) => `
                <div class="standard-time__history-item">
                    <div class="standard-time__history-title">${escapeHtml(history.text_name ?? '不明なテキスト')}</div>
                    <div class="standard-time__history-meta">
                        ${escapeHtml(history.course_name ?? '不明なコース')} / ${escapeHtml(history.chapter_name ?? '不明な章')}
                    </div>
                    <div class="standard-time__history-meta">
                        ${escapeHtml(formatMinutes(history.old_minutes ?? 0))} → ${escapeHtml(formatMinutes(history.new_minutes ?? 0))}
                    </div>
                    <div class="standard-time__history-meta">
                        更新者: ${escapeHtml(history.updated_by ?? '不明')} / ${escapeHtml(history.updated_at ?? '')}
                    </div>
                </div>
            `).join('')
            : '<div class="standard-time__empty">まだ更新履歴がありません。</div>';

        this.root.innerHTML = `
            ${alerts}
            <div class="standard-time">
                <div class="standard-time__panel standard-time__panel--editor">
                    <div class="standard-time__panel-header">
                        <h4 class="standard-time__panel-title">コース別 基準時間</h4>
                        <div class="text-muted small">コースと章を選択し、テキスト単位で基準時間を設定します。</div>
                    </div>
                    <div class="standard-time__field-group">
                        <div class="standard-time__selectors">
                            <label class="form-label">コース
                                <select class="form-select" data-action="change-course">
                                    <option value="">選択してください</option>
                                    ${courseOptions}
                                </select>
                            </label>
                            <label class="form-label">章
                                <select class="form-select" data-action="change-chapter" ${chapterSelectDisabled ? 'disabled' : ''}>
                                    <option value="">選択してください</option>
                                    ${chapterOptions}
                                </select>
                            </label>
                        </div>
                        <button type="button"
                                class="standard-time__button standard-time__button--primary standard-time__button--inline"
                                data-action="save"
                                ${this.state.saving ? 'disabled' : ''}>
                            ${this.state.saving ? '保存中...' : '基準時間を保存'}
                        </button>
                    </div>
                    <div class="standard-time__table-wrapper">
                        <div class="standard-time__table-scroll">
                            <table class="standard-time__table">
                                <thead>
                                    <tr>
                                        <th style="width: 60px;">#</th>
                                        <th>テキスト</th>
                                        <th style="width: 80px;">章</th>
                                        <th style="width: 100px;">順序</th>
                                        <th style="width: 160px;">基準時間 (分)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    ${rows}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div class="standard-time__summary">
                        <div class="standard-time__summary-item">
                            <div class="standard-time__summary-label">テキスト数</div>
                            <div class="standard-time__summary-value" data-summary="count">${escapeHtml(String(summary.textCount))} 件</div>
                        </div>
                        <div class="standard-time__summary-item">
                            <div class="standard-time__summary-label">合計時間</div>
                            <div class="standard-time__summary-value" data-summary="total">${escapeHtml(formatMinutes(summary.totalMinutes))}</div>
                        </div>
                        <div class="standard-time__summary-item">
                            <div class="standard-time__summary-label">平均時間</div>
                            <div class="standard-time__summary-value" data-summary="average">${escapeHtml(formatMinutes(summary.average))}</div>
                        </div>
                    </div>
                </div>
                <div class="standard-time__panel standard-time__panel--history">
                    <div class="standard-time__panel-header">
                        <h4 class="standard-time__panel-title">更新履歴 (最新10件)</h4>
                    </div>
                    <div class="standard-time__list">${historyItems}</div>
                </div>
            </div>
        `;

        this.root.querySelectorAll('.standard-time__input').forEach((input) => {
            input.addEventListener('input', (event) => this.handleMinutesChange(event));
        });

        const saveButton = this.root.querySelector('[data-action="save"]');
        if (saveButton) {
            saveButton.addEventListener('click', () => this.handleSave());
        }

        const courseSelect = this.root.querySelector('[data-action="change-course"]');
        if (courseSelect) {
            courseSelect.addEventListener('change', (event) => this.handleCourseChange(event));
        }

        const chapterSelect = this.root.querySelector('[data-action="change-chapter"]');
        if (chapterSelect) {
            chapterSelect.addEventListener('change', (event) => this.handleChapterChange(event));
        }
    }
}

export default StandardTimeApp;
